import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, ArrowLeft, Sparkles, Phone, Mail, Wrench, Trees, Hammer, Droplet, Zap, Home as HomeIcon, CalendarOff, Trash2 } from "lucide-react";
import { toast } from "sonner";

const DOW = [
  { k: 0, label: "Mon" }, { k: 1, label: "Tue" }, { k: 2, label: "Wed" },
  { k: 3, label: "Thu" }, { k: 4, label: "Fri" }, { k: 5, label: "Sat" }, { k: 6, label: "Sun" },
];

// Phase 53 — trade categories. Order matters: housekeeping is the legacy
// default and stays first.
export const TRADES = [
  { k: "housekeeping",   label: "Housekeeping",   icon: HomeIcon, color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { k: "groundskeeping", label: "Groundskeeping", icon: Trees,    color: "bg-lime-100 text-lime-800 border-lime-200" },
  { k: "maintenance",    label: "Maintenance",    icon: Wrench,   color: "bg-slate-100 text-slate-800 border-slate-200" },
  { k: "plumbing",       label: "Plumbing",       icon: Droplet,  color: "bg-sky-100 text-sky-800 border-sky-200" },
  { k: "electrical",     label: "Electrical",     icon: Zap,      color: "bg-amber-100 text-amber-800 border-amber-200" },
  { k: "carpentry",      label: "Carpentry",      icon: Hammer,   color: "bg-orange-100 text-orange-800 border-orange-200" },
];
export const tradeMeta = (k) => TRADES.find((t) => t.k === k) || TRADES[0];

// Phase 54 — leave kinds. Stripe colour shows up on the calendar overlay.
export const LEAVE_KINDS = [
  { k: "annual",     label: "Annual leave",    color: "bg-amber-100 text-amber-900 border-amber-300",     stripe: "bg-amber-500" },
  { k: "maternity",  label: "Maternity",       color: "bg-pink-100 text-pink-900 border-pink-300",         stripe: "bg-pink-500" },
  { k: "paternity",  label: "Paternity",       color: "bg-violet-100 text-violet-900 border-violet-300",   stripe: "bg-violet-500" },
  { k: "sick",       label: "Sick leave",      color: "bg-red-100 text-red-900 border-red-300",            stripe: "bg-red-500" },
  { k: "other",      label: "Other / unpaid",  color: "bg-slate-100 text-slate-900 border-slate-300",      stripe: "bg-slate-500" },
];
export const leaveMeta = (k) => LEAVE_KINDS.find((l) => l.k === k) || LEAVE_KINDS[0];

export default function Cleaners() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  // Phase 54 — leave management
  const [leaveStaff, setLeaveStaff] = useState(null);     // cleaner record being managed
  const [leaves, setLeaves] = useState([]);                // list for that cleaner
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveDraft, setLeaveDraft] = useState(null);     // {kind, from_date, to_date, notes, forecasted, id?}
  const isAdmin = ["admin", "global_admin"].includes(user.role);
  const canManage = ["admin", "global_admin", "hospitality_coordinator", "booking_coordinator"].includes(user.role);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/housing/cleaners"); setItems(data); }
    catch { /* */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Name required"); return; }
    try {
      if (editing.id) await api.put(`/housing/cleaners/${editing.id}`, editing);
      else await api.post("/housing/cleaners", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  // Phase 54 — leave management handlers
  const openLeaves = async (staff) => {
    setLeaveStaff(staff);
    setLeaveLoading(true);
    setLeaves([]);
    try {
      const { data } = await api.get(`/housing/cleaners/${staff.id}/leaves`);
      setLeaves(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load leaves");
    } finally { setLeaveLoading(false); }
  };
  const newLeave = () => setLeaveDraft({
    kind: "annual",
    from_date: new Date().toISOString().slice(0, 10),
    to_date: new Date().toISOString().slice(0, 10),
    notes: "",
    forecasted: true,
  });
  const saveLeave = async () => {
    if (!leaveStaff || !leaveDraft) return;
    if (!leaveDraft.from_date || !leaveDraft.to_date) { toast.error("Pick start and end dates"); return; }
    try {
      const url = leaveDraft.id
        ? `/housing/cleaners/${leaveStaff.id}/leaves/${leaveDraft.id}`
        : `/housing/cleaners/${leaveStaff.id}/leaves`;
      const fn = leaveDraft.id ? api.put : api.post;
      await fn(url, leaveDraft);
      toast.success(leaveDraft.id ? "Leave updated" : "Leave added");
      setLeaveDraft(null);
      const { data } = await api.get(`/housing/cleaners/${leaveStaff.id}/leaves`);
      setLeaves(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  const deleteLeave = async (lv) => {
    if (!window.confirm(`Delete this ${leaveMeta(lv.kind).label.toLowerCase()} from ${lv.from_date} → ${lv.to_date}?`)) return;
    try {
      await api.delete(`/housing/cleaners/${leaveStaff.id}/leaves/${lv.id}`);
      toast.success("Leave removed");
      setLeaves((prev) => prev.filter((x) => x.id !== lv.id));
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="cleaners-page">
      <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-[#525C58]">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
      </Button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Operations Staff</h1>
          <p className="text-[#525C58] mt-1">House helpers, groundskeepers, maintenance, plumbing, electrical & more — all assignable to bookings, work orders and the Operations Calendar.</p>
        </div>
        {canManage && (
          <Button onClick={() => setEditing({ name: "", phone: "", email: "", notes: "", active: true, working_days: [0, 1, 2, 3, 4], default_shift: "all_day", categories: ["housekeeping"] })} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="new-cleaner-btn">
            <Plus className="h-4 w-4 mr-2" /> Add staff
          </Button>
        )}
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-[#40916C]" /> Staff ({items.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <div className="py-10 text-center text-[#7A8A84]">Loading…</div> : items.length === 0 ? (
            <p className="text-[#7A8A84] text-center py-10">No helpers yet.</p>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {items.map(c => (
                <div key={c.id} className={`p-4 rounded-lg border ${c.active ? "border-[#E8E8E6] bg-white" : "border-red-100 bg-red-50/40 opacity-70"}`} data-testid={`cleaner-card-${c.id}`}>
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="font-semibold text-[#1C1F1E]">{c.name}</h3>
                    {!c.active && <Badge className="bg-red-100 text-red-800 border-0 text-[10px]">Inactive</Badge>}
                  </div>
                  {(c.categories || ["housekeeping"]).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5 mb-1.5" data-testid={`cleaner-trades-${c.id}`}>
                      {(c.categories || ["housekeeping"]).map((k) => {
                        const m = tradeMeta(k);
                        const Icon = m.icon;
                        return (
                          <span key={k} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${m.color}`}>
                            <Icon className="h-2.5 w-2.5" /> {m.label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {c.phone && <p className="text-xs text-[#7A8A84] flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</p>}
                  {c.email && <p className="text-xs text-[#7A8A84] flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</p>}
                  {c.notes && <p className="text-xs text-[#525C58] mt-2 line-clamp-2">{c.notes}</p>}
                  <Button size="sm" variant="ghost" onClick={() => setEditing({ ...c, categories: c.categories || ["housekeeping"] })} className="text-[#1B4332] mt-2 -ml-2" data-testid={`edit-cleaner-${c.id}`}>
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openLeaves(c)} className="text-[#525C58] mt-2" data-testid={`leaves-cleaner-${c.id}`}>
                    <CalendarOff className="h-3.5 w-3.5 mr-1" /> Leave
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit helper" : "Add helper"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="cleaner-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editing.email || ""}
                  onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                  placeholder="for schedule emails (optional)"
                  data-testid="cleaner-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Trades / categories</Label>
                <div className="flex flex-wrap gap-1.5" data-testid="trades-picker">
                  {TRADES.map((t) => {
                    const on = (editing.categories || ["housekeeping"]).includes(t.k);
                    const Icon = t.icon;
                    return (
                      <button
                        key={t.k} type="button"
                        onClick={() => {
                          const cur = new Set(editing.categories || ["housekeeping"]);
                          if (cur.has(t.k)) cur.delete(t.k); else cur.add(t.k);
                          if (cur.size === 0) cur.add("housekeeping");
                          setEditing({ ...editing, categories: [...cur] });
                        }}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                          on ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white text-[#525C58] border-[#E8E8E6] hover:bg-[#F4F4F1]"
                        }`}
                        data-testid={`trade-${t.k}`}
                      >
                        <Icon className="h-3 w-3" /> {t.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-[#9AA39E]">A staff member can carry multiple trades. The Operations Calendar filters on these tags.</p>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Working days</Label>
                <div className="flex flex-wrap gap-1.5" data-testid="working-days-picker">
                  {DOW.map((d) => {
                    const on = (editing.working_days || [0, 1, 2, 3, 4]).includes(d.k);
                    return (
                      <button
                        key={d.k} type="button"
                        onClick={() => {
                          const cur = new Set(editing.working_days || [0, 1, 2, 3, 4]);
                          if (cur.has(d.k)) cur.delete(d.k); else cur.add(d.k);
                          setEditing({ ...editing, working_days: [...cur].sort() });
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                          on ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white text-[#525C58] border-[#E8E8E6] hover:bg-[#F4F4F1]"
                        }`}
                        data-testid={`wd-${d.k}`}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-[#9AA39E]">Days they're rostered. Helpers won't appear as "available" outside these days.</p>
              </div>
              <div className="space-y-2">
                <Label>Default shift</Label>
                <Select value={editing.default_shift || "all_day"} onValueChange={(v) => setEditing({ ...editing, default_shift: v })}>
                  <SelectTrigger data-testid="default-shift"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning">Morning</SelectItem>
                    <SelectItem value="afternoon">Afternoon</SelectItem>
                    <SelectItem value="all_day">All day</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <Label>Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="save-cleaner-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 54 — Leave management */}
      <Dialog open={!!leaveStaff} onOpenChange={(o) => { if (!o) { setLeaveStaff(null); setLeaves([]); setLeaveDraft(null); } }}>
        <DialogContent className="max-w-xl" data-testid="leaves-dialog">
          <DialogHeader>
            <DialogTitle>Leave — {leaveStaff?.name}</DialogTitle>
            <p className="text-xs text-[#7A8A84]">Annual leave, maternity, paternity, sick, or other absences. These dates will appear on the Operations Calendar and exclude this person from auto-assignment.</p>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={newLeave} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="new-leave-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add leave
              </Button>
            </div>
            {leaveLoading ? (
              <div className="py-6 text-center text-sm text-[#7A8A84]">Loading…</div>
            ) : leaves.length === 0 ? (
              <p className="py-6 text-center text-sm text-[#7A8A84]" data-testid="no-leaves">No leave on file.</p>
            ) : (
              <div className="border rounded-lg divide-y divide-[#E8E8E6] max-h-72 overflow-y-auto">
                {leaves.map((lv) => {
                  const m = leaveMeta(lv.kind);
                  return (
                    <div key={lv.id} className="flex items-center gap-3 p-2.5" data-testid={`leave-row-${lv.id}`}>
                      <span className={`inline-block w-1.5 h-9 rounded-sm ${m.stripe}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`${m.color} border text-[10px]`}>{m.label}</Badge>
                          {!lv.forecasted && <Badge className="bg-amber-50 text-amber-800 border border-amber-200 text-[10px]">As-needed</Badge>}
                        </div>
                        <p className="text-sm text-[#1C1F1E] mt-0.5">{lv.from_date} → {lv.to_date}</p>
                        {lv.notes && <p className="text-xs text-[#7A8A84] line-clamp-1">{lv.notes}</p>}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => setLeaveDraft({ ...lv })} className="h-7 w-7 p-0 text-[#1B4332]" data-testid={`edit-leave-${lv.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteLeave(lv)} className="h-7 w-7 p-0 text-red-600" data-testid={`delete-leave-${lv.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!leaveDraft} onOpenChange={(o) => { if (!o) setLeaveDraft(null); }}>
        <DialogContent className="max-w-md" data-testid="leave-draft-dialog">
          <DialogHeader>
            <DialogTitle>{leaveDraft?.id ? "Edit leave" : "Add leave"}</DialogTitle>
          </DialogHeader>
          {leaveDraft && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={leaveDraft.kind} onValueChange={(v) => setLeaveDraft({ ...leaveDraft, kind: v })}>
                  <SelectTrigger data-testid="leave-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEAVE_KINDS.map((k) => (
                      <SelectItem key={k.k} value={k.k}>{k.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>From</Label>
                  <Input type="date" value={leaveDraft.from_date} onChange={(e) => setLeaveDraft({ ...leaveDraft, from_date: e.target.value })} data-testid="leave-from-date" />
                </div>
                <div className="space-y-2">
                  <Label>To (inclusive)</Label>
                  <Input type="date" value={leaveDraft.to_date} onChange={(e) => setLeaveDraft({ ...leaveDraft, to_date: e.target.value })} data-testid="leave-to-date" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  rows={2}
                  value={leaveDraft.notes || ""}
                  onChange={(e) => setLeaveDraft({ ...leaveDraft, notes: e.target.value })}
                  placeholder="Optional context (doctor's note ref, return-to-work, etc.)"
                  data-testid="leave-notes-input"
                />
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!leaveDraft.forecasted}
                  onChange={(e) => setLeaveDraft({ ...leaveDraft, forecasted: e.target.checked })}
                  className="mt-1 h-4 w-4 rounded border-[#7A8A84] text-[#1B4332] focus:ring-[#1B4332]"
                  data-testid="leave-forecasted-checkbox"
                />
                <span className="text-sm">
                  <span className="font-medium text-[#1C1F1E]">Forecasted (planned in advance)</span>
                  <span className="block text-xs text-[#7A8A84]">Untick for as-needed (sudden sick day, emergency, etc.)</span>
                </span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveDraft(null)}>Cancel</Button>
            <Button onClick={saveLeave} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="save-leave-btn">Save leave</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
